'use client';
import React from 'react';
import { Lock } from 'lucide-react';
import { MILESTONES, type MilestoneStats } from '@/lib/milestones';

interface MilestoneBadgesProps {
  stats: MilestoneStats;
  compact?: boolean;
}

export default function MilestoneBadges({ stats, compact = false }: MilestoneBadgesProps) {
  return (
    <div className={compact ? 'flex flex-wrap gap-2' : 'grid grid-cols-1 sm:grid-cols-3 gap-4'}>
      {MILESTONES.map((milestone) => {
        const earned = milestone.check(stats);
        const { current, target } = milestone.progress(stats);
        const pct = Math.round((current / target) * 100);

        if (compact) {
          return (
            <div
              key={milestone.id}
              title={earned ? `${milestone.title} — Unlocked!` : `${milestone.title} — ${current}/${target}`}
              className="relative flex items-center justify-center w-10 h-10 rounded-full text-xl transition-all"
              style={{
                background: earned ? milestone.bgColor : 'rgba(0,0,0,0.05)',
                border: `2px solid ${earned ? milestone.borderColor : 'rgba(0,0,0,0.1)'}`,
                filter: earned ? 'none' : 'grayscale(1)',
                opacity: earned ? 1 : 0.5,
              }}
            >
              {earned ? (
                <span>{milestone.emoji}</span>
              ) : (
                <Lock size={14} className="text-muted-foreground" />
              )}
            </div>
          );
        }

        return (
          <div
            key={milestone.id}
            className="relative rounded-2xl p-4 flex flex-col items-center text-center gap-2 transition-all duration-300 overflow-hidden"
            style={{
              background: earned ? milestone.bgColor : 'rgba(0,0,0,0.03)',
              border: `1.5px solid ${earned ? milestone.borderColor : 'rgba(0,0,0,0.08)'}`,
            }}
          >
            {/* Glow effect when earned */}
            {earned && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: `radial-gradient(circle at 50% 0%, ${milestone.bgColor} 0%, transparent 70%)`,
                }}
              />
            )}

            {/* Badge icon */}
            <div
              className="relative w-14 h-14 rounded-2xl flex items-center justify-center text-3xl transition-all"
              style={{
                background: earned ? milestone.bgColor : 'rgba(0,0,0,0.06)',
                border: `2px solid ${earned ? milestone.borderColor : 'transparent'}`,
                filter: earned ? 'none' : 'grayscale(1)',
                opacity: earned ? 1 : 0.4,
              }}
            >
              {earned ? (
                <>
                  <span>{milestone.emoji}</span>
                  {/* Sparkle */}
                  <span className="absolute -top-1 -right-1 text-xs">✨</span>
                </>
              ) : (
                <Lock size={20} className="text-muted-foreground" />
              )}
            </div>

            {/* Title */}
            <div className="relative">
              <p
                className="text-sm font-700 leading-tight"
                style={{ color: earned ? milestone.color : 'var(--muted-foreground)' }}
              >
                {milestone.title}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{milestone.description}</p>
            </div>

            {/* Progress bar */}
            {!earned && (
              <div className="relative w-full">
                <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{ width: `${pct}%`, background: milestone.color }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {current}/{target}
                </p>
              </div>
            )}

            {/* Earned label */}
            {earned && (
              <span
                className="relative text-xs font-700 px-2.5 py-0.5 rounded-full"
                style={{ background: milestone.bgColor, color: milestone.color }}
              >
                Unlocked!
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
